import { Component, inject, OnInit, signal } from '@angular/core';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../../@shared/components/table-component/table-component';
import { ActivatedRoute, Router } from '@angular/router';
import { Params } from '../../../../services/utilis/add-params-service';
import { PaginatorState } from 'primeng/paginator';
import { TelemetryService } from '../../../../services/iot/telemetry-service';
import { Telemetry } from '../../../../models/telemetry';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs';
import { FormControl } from '@angular/forms';
import { AddIotForm } from '../../../../@shared/forms/iot/add-iot-form/add-iot-form';
import { UpdateIotForm } from '../../../../@shared/forms/iot/update-iot-form/update-iot-form';
import { DeleteIotForm } from '../../../../@shared/forms/iot/delete-iot-form/delete-iot-form';
import { ShowLocationDialog } from '../../../../@shared/dialogs/show-location-dialog/show-location-dialog';
import { EngineStatus } from '../../../../models/enums';
import { enumToOptions } from '../../../../@shared/utility/enumToOptions';

@Component({
  selector: 'app-manag-iot-list',
  imports: [TableComponent, AddIotForm, UpdateIotForm, DeleteIotForm, ShowLocationDialog],
  templateUrl: './manag-iot-list.html',
  styleUrl: './manag-iot-list.scss'
})
export class ManagIotList implements OnInit {

  hasCarFilter: CustomFilter = {
    key: 'hasCar',
    label: 'Has Car',
    type: 'select',
    options: [
      { label: 'YES', value: true },
      { label: 'NO', value: false }
    ]
  }

  params = [
    "page",
    "size",
    "search",
    'sortBy',
    'sortDirection',
    'minFuelLevel',
    'maxFuelLevel',
    'engineStatus',
    'hasCar'
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    search: "search",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    minFuelLevel: "minFuelLevel",
    maxFuelLevel: "maxFuelLevel",
    engineStatus: "engineStatus",
    hasCar: "hasCar"
  }

  // Define columns to show in the table
  tableColumns: DynamicColumn<Telemetry>[] = [
    {
      header: 'ID',
      field: 'id',
      sortable: true
    },
    { header: 'IMEI', field: 'imei', sortable: true, },
    { header: 'Iot model', field: 'iotModel', sortable: true, },
    {
      header: 'Engine status',
      field: 'engineStatus',
      sortable: true,
      isFilterable: true,
      filterType: 'multiselect',
      filterOptions: enumToOptions(EngineStatus),
      cellClass: (value) => {
        switch (value) {
          case 'ON':  return 'text-green';
          case 'OFF': return 'text-red';
          default:    return '';
        }
      }
    },
    {
      header: 'Fuel',
      field: 'fuelLevel',
      suffixText: "%",
      sortable: true,
      isFilterable: true,
      filterType: 'slider',
      min: 0,
      max: 100,
      step: 1,
    },
    {
      field: 'timestamp',
      header: 'Location',
      sortable: false,
      locationCell: true,
      valueGetter: () => null,
      command: (iot) => this.openLocationDialog(iot),
    },
    {
      header: 'Power supply voltage',
      field: 'powerSupplyVoltage',
      sortable: true,
      valueGetter: (row) => {
        if (!row.powerSupplyVoltage) return null;

        return Math.min((row.powerSupplyVoltage / 12) * 100, 100).toFixed(0);
      }
    },
    { header: 'Speed', field: 'speed', sortable: true, },
    { header: 'Odometer', field: 'odometer', sortable: true, },
    { header: 'Date', field: 'timestamp', isBrowserTime: true,  sortable: false },
    {
      field: 'actions',
      header: 'Actions',
      sortable: false,
      actions: [
        {
          icon: 'edit-icon.svg',
          command: (iot) => this.openUpdateIotDialog(iot),
          buttonClass: 'primary-bg-color',
          tooltip: 'Update',
        },
        {
          icon: 'trash-icon.svg',
          command: (iot) => this.openDeleteIotDialog(iot),
          buttonClass: 'danger-bg-color',
          tooltip: 'Delete',
        },
      ],
    },
  ];

  IOTList = signal<Telemetry[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;

  searchControl = new FormControl('');

  selectedIOT: Telemetry
  addIotDialog = false
  updateIotDialog = false
  deleteIotDialog = false
  locationDialog = false

  private telemetryService = inject(TelemetryService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)

  ngOnInit() {
    this.getActivityLogs()
  }

  getActivityLogs = () => {
    this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),

      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'updatedAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.telemetryService.getTelemetryList(params);
      })
    )
      .subscribe((res) => {
        this.IOTList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  }

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  openAddIotDialog = () => {
    this.addIotDialog = true
  }

  onIotAdded = () => {
    this.getActivityLogs()
    this.addIotDialog = false
  }

  openUpdateIotDialog = (selectedRow: Telemetry) => {
    this.selectedIOT = selectedRow
    this.updateIotDialog = true
  }

  onIotUpdated = () => {
    this.getActivityLogs()
    this.updateIotDialog = false;
  };

  openDeleteIotDialog = (selectedRow: Telemetry) => {
    this.selectedIOT = selectedRow
    this.deleteIotDialog = true
  }

  onIotDeleted = () => {
    this.getActivityLogs()
    this.deleteIotDialog = false
  }

  openLocationDialog = (selectedRow: Telemetry) => {
    this.selectedIOT = selectedRow
    const url = `https://www.google.com/maps/search/?api=1&query=${selectedRow.position?.latitude ?? 0},${selectedRow.position?.longitude ?? 0}`;
    window.open(url, '_blank');

  }
}
